import React from 'react';
import { Box, HStack, Text, Tooltip } from '@chakra-ui/react';
import { pxToRem, colorTheme } from '@codametrix/themes/chakra/biome';
import { CMXIcon } from '@codametrix/ui-components';

// statusColors.CANCEL is the shared amber (#f5b940). Complete uses the shared
// success green from colorTheme so the chips read consistently with the app.
const AMBER: string = '#f5b940'; // shared warning amber (was commonEnums.statusColors.CANCEL) — accent only (border/bg)
const AMBER_TEXT: string = '#8a5a00'; // AA-contrast amber for text/icon (#f5b940 is ~1.6:1 as text, fails WCAG AA)
const GREEN: string = colorTheme.success[500]; // '#42A45C' — accent only (border/bg)
const GREEN_TEXT: string = '#2A6B3C'; // AA-contrast green for label text (GREEN #42A45C is ~3.1:1 as text)

// [CDMX-17055/17056] A span is INCOMPLETE only when the server explicitly flags it via
// workflowComplete === false — i.e. it still has unanswered REQUIRED span-scoped workflow
// questions, computed server-side by SubmissionValidationService.computeSpanWorkflowCompletion.
// Spans the server did not evaluate (validation off, no workflow, older payloads) have
// workflowComplete undefined/true and therefore read as complete. This replaces the old
// CDMX-14713 "has any rubric attribute" heuristic, which mis-flagged partially-answered spans.
export const isSpanIncomplete = (span: Accelerate.SpanAnnotation): boolean =>
  span.workflowComplete === false;

/**
 * Human-readable reference for a span: a truncated preview of its covered text
 * when present, otherwise a stable `Span #<id>` fallback.
 */
export const spanLabel = (span: Accelerate.SpanAnnotation | undefined | null, max = 40): string => {
  if (!span) return 'Span';
  const text = span.coveredText?.trim();
  if (text) {
    return text.length > max ? `${text.slice(0, max)}…` : text;
  }
  return `Span #${span.id}`;
};

export interface SpanStatusChipProps {
  span: Accelerate.SpanAnnotation;
  /** Render only the icon (compact inline usage near a span section). */
  iconOnly?: boolean;
}

/**
 * A single per-span status chip: green check when complete, amber warning when
 * the span still needs work.
 */
export const SpanStatusChip: React.FC<SpanStatusChipProps> = ({ span, iconOnly = false }) => {
  const incomplete = isSpanIncomplete(span);
  const color = incomplete ? AMBER : GREEN; // accent for border + bg tint
  const textColor = incomplete ? AMBER_TEXT : GREEN_TEXT; // AA-safe foreground for the label (the success/check icon fill is hardcoded in svg-icons.tsx — tracked as follow-up)
  const label = incomplete ? 'Needs work' : 'Complete';
  const iconName = incomplete ? 'BiomeWarning' : 'BiomeCheckCircle';

  const chip = (
    <HStack
      className={`qa-span-status-chip ${incomplete ? 'qa-span-status-incomplete' : 'qa-span-status-complete'}`}
      spacing={1}
      px={iconOnly ? 0 : 2}
      py={iconOnly ? 0 : '2px'}
      borderRadius="full"
      border={iconOnly ? 'none' : `1px solid ${color}`}
      bg={iconOnly ? 'transparent' : `${color}22`}
      display="inline-flex"
      alignItems="center"
    >
      <Box as="span" color={textColor} display="inline-flex" alignItems="center">
        <CMXIcon svgIconName={iconName as any} svgIconSize="16" svgTitleText={label} />
      </Box>
      {!iconOnly && (
        <Text fontSize={pxToRem(12)} fontWeight="semibold" color={textColor}>
          {label}
        </Text>
      )}
    </HStack>
  );

  return iconOnly ? <Tooltip label={label}>{chip}</Tooltip> : chip;
};

export default SpanStatusChip;
